import { createContext, useContext } from 'react';

export interface DrawerContextValue {
  /**
   * The size of the drawer.
   *
   * In addition to the dimensions of the drawer, size also impacts the following styling:
   * - `background`: 'small' drawers use the 'container' background color. Other sizes use the 'canvas'
   *   background color.
   *
   * @default 'medium'
   */
  size: 'small' | 'medium' | 'large' | 'large-fluid';

  /**
   * Determines the action buttons in the header and footer.
   *
   * - `create`: Shows "Create" and "Cancel" buttons in the header.
   * - `edit`: Shows "Save" and "Cancel" buttons in the header and a "Delete" button in the footer.
   * - `detail`: Shows a close IconButton in the heaader.
   *
   * Use `title` prop to set the header title.
   *
   * @default 'edit'
   */
  variant: 'create' | 'edit' | 'detail';
}

const DrawerContext = createContext<null | DrawerContextValue>(null);

export interface DrawerContextProviderProps {
  children: React.ReactNode;
  value: DrawerContextValue;
}

export function DrawerContextProvider({ children, value }: DrawerContextProviderProps) {
  return <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>;
}

export function useDrawerContext() {
  return useContext(DrawerContext);
}

/**
 * Returns drawer context, if available. Otherwise throws an error.
 */
export function useRequiredDrawerContext() {
  const context = useDrawerContext();

  if (!context) {
    throw new Error(
      'Missing DrawerContextProvider. This likely means you are using a subcomponent of `Drawer` without wrapping it in a `Drawer`.'
    );
  }
  return context;
}
